import { ChangeEvent, useMemo } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { TEXT_POSITION_OPTIONS } from "@constants/text-position-options";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { SelectOption } from "@idw-types/common";
import {
    AXNUIContent,
    AXNUIContentHeader,
    AXNUITheme,
    AXNUIThemeHeader
} from "@iddataweb/idw-common-ui/types";
import { ThemingHeaderValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { RadioInputFormGroup } from "../../../../../common/radio-input-form-group/radio-input-form-group";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { SelectFormGroup } from "../../../../../common/select-form-group/select-form-group";
import { ColorPickerFormGroup } from "../../../../../common/color-picker-form-group/color-picker-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingHeaderTextSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    axnuiContent: AXNUIContent,
    axnuiTheme: AXNUITheme;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    validationErrors: ThemingHeaderValidationErrors;
    updateAXNUIContentHeaderFieldValue: (key: keyof AXNUIContentHeader, value: string) => void;
    updateAXNUIThemeHeaderFieldValue: (key: keyof AXNUIThemeHeader, value: string | boolean) => void;
};

export const ThemingHeaderTextSection = (props: ThemingHeaderTextSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        axnuiContent,
        axnuiTheme,
        inheritedAXNUIContent,
        inheritedAXNUITheme,
        pageLevel,
        validationErrors,
        updateAXNUIContentHeaderFieldValue,
        updateAXNUIThemeHeaderFieldValue
    } = props;


    const handleAddHeaderTextChange = (newValue: string) => {
        updateAXNUIThemeHeaderFieldValue("showText", isStringTrue(newValue));
    };

    const handleResetAddHeaderText = () => {
        updateAXNUIThemeHeaderFieldValue("showText", inheritedAXNUITheme.header.showText);
    };

    const handleHeaderTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentHeaderFieldValue("text", event.target.value);
    };

    const handleResetHeaderText = () => {
        updateAXNUIContentHeaderFieldValue("text", inheritedAXNUIContent.header.text);
    };

    const handleHeaderTextPositionChange = (newValue: SelectOption) => {
        updateAXNUIThemeHeaderFieldValue("alignText", newValue.label);
    };

    const handleResetHeaderTextPosition = () => {
        updateAXNUIThemeHeaderFieldValue("alignText", inheritedAXNUITheme.header.alignText);
    };

    const handleHeaderTextColorChange = (newValue: string) => {
        updateAXNUIThemeHeaderFieldValue("textColor", newValue);
    };

    const handleResetHeaderTextColor = () => {
        updateAXNUIThemeHeaderFieldValue("textColor", inheritedAXNUITheme.header.textColor);
    };

    const selectedHeaderTextPositionOption = useMemo(() => {
        if (axnuiTheme.header.alignText) {
            return TEXT_POSITION_OPTIONS.find(po => po.label === axnuiTheme.header.alignText);
        }
        return null;
    }, [axnuiTheme.header.alignText]);

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.header.showText}
                currentThemingPropertyValue={axnuiTheme.header.showText}
                pageLevel={pageLevel}
            >
                <RadioInputFormGroup
                    controlId={`theming-header-add-header-text-radio-input`}
                    automationId={`theming-header-add-header-text-radio-input`}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerText.inputs.addHeaderText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleAddHeaderTextChange}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    value={axnuiTheme.header.showText ? "true" : "false"}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerText.inputs.addHeaderText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetAddHeaderText}
                />
            </ThemingFormGroupWrapper>
            {axnuiTheme.header.showText && (
                <>
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUIContent.header.text}
                        currentThemingPropertyValue={axnuiContent.header.text}
                        pageLevel={pageLevel}
                    >
                        <InputFormGroup
                            controlId={"theming-header-header-text-input"}
                            automationId={"theming-header-header-text-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerText.inputs.headerText.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerText.inputs.headerText.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleHeaderTextChange}
                            value={axnuiContent.header.text || ""}
                            disabled={isDisabled}
                            maxLength={30}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerText.inputs.headerText.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetHeaderText}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.headerTextError}

                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUITheme.header.alignText}
                        currentThemingPropertyValue={axnuiTheme.header.alignText}
                        pageLevel={pageLevel}
                    >
                        <SelectFormGroup
                            controlId={"theming-header-header-text-postition-select-input"}
                            automationId={"theming-header-header-text-position-select-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerText.inputs.headerTextPosition.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerText.inputs.headerTextPosition.label}
                            disabled={isDisabled}
                            isClearable={false}
                            isRequired={true}
                            isSearchable={false}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleHeaderTextPositionChange}
                            options={TEXT_POSITION_OPTIONS}
                            selectedOption={selectedHeaderTextPositionOption}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerText.inputs.headerTextPosition.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetHeaderTextPosition}
                        />
                    </ThemingFormGroupWrapper>

                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUITheme.header.textColor}
                        currentThemingPropertyValue={axnuiTheme.header.textColor}
                        pageLevel={pageLevel}
                    >
                        <ColorPickerFormGroup
                            controlId={"theming-header-header-text-color-picker"}
                            automationId={"theming-header-header-text-color-picker"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerText.inputs.headerTextColor.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerText.inputs.headerTextColor.label}
                            disabled={isDisabled}
                            isRequired={true}
                            layout={layout}
                            labelWidth={labelWidth}
                            value={axnuiTheme.header.textColor || ""}
                            onChange={handleHeaderTextColorChange}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerText.inputs.headerTextColor.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetHeaderTextColor}
                        />
                    </ThemingFormGroupWrapper>
                </>
            )}
        </div>
    );
};